<template>
  <div>
    <Modal
      width="400"
      :value="overrideModalOpen"
      dialogIcon="danger"
      :ctaText="$t('resources.domains.override_domain_modal_confirm')"
      @confirm="confirmOverride"
      @cancel="overrideModalOpen = false">
      <template #default>
        <div class="content-override-modal-title">
          {{ $t("resources.domains.override_domain_content_warning_title") }}
        </div>
        <div class="content-override-modal-content">
          {{ $t("resources.domains.override_domain_content_warning_content") }}
        </div>
      </template>
    </Modal>
    <va-form v-if="step === 1" ref="form" :item="domain" v-model="form" :requiredFieldsSet="requiredFieldsSet">
      <v-card class="spacing-bottom--24">
        <v-card-title class="spacing-bottom--16">{{ $t("resources.domains.registrar_title") }} </v-card-title>
        <v-select
          v-model="form.registrar"
          :placeholder="$t('resources.domains.registrar_input_placeholder')"
          :items="REGISTRAR_LIST"
          item-text="name"
          return-object
          hide-details="auto"
          persistent-placeholder
          :rules="[v => !!v || this.$t('va.forms.required_field', { field: 'Registrar' })]"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }">
          <template #append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
          <template #label>
            <div class="input-label">
              {{ $t("resources.domains.registrar_input_label") }}<span class="required__icon">*</span>
            </div>
          </template>
        </v-select>
      </v-card>
      <v-card class="spacing-bottom--24 radio-card-wrapper">
        <v-card-title class="spacing-bottom--16">{{ $t("resources.domains.existing_content_title") }} </v-card-title>
        <v-radio-group
          :class="['radio-wrapper', { 'spacing-bottom--24': existingContent === 'yes' }]"
          :label="$t('resources.domains.existing_content_question')"
          v-model="existingContent"
          @change="$emit('isDirtyForm', true)"
          :rules="[rules.required]"
          hide-details="auto">
          <v-radio class="radio-input" value="yes" :label="$t('resources.domains.existing_content_question_yes')">
          </v-radio>
          <v-radio class="radio-input" value="no" :label="$t('resources.domains.existing_content_question_no')">
          </v-radio>
        </v-radio-group>
        <v-radio-group
          v-if="existingContent === 'yes'"
          v-model="override"
          class="radio-wrapper"
          :label="$t('resources.domains.existing_content_question_2')"
          hide-details="auto"
          :rules="[rules.required]">
          <v-radio
            value="yes"
            class="radio-input"
            :label="$t('resources.domains.existing_content_question_2_override')">
          </v-radio>
          <v-radio
            value="no"
            class="radio-input"
            :label="$t('resources.domains.existing_content_question_2_subdomain')">
          </v-radio>
        </v-radio-group>
      </v-card>

      <v-card class="spacing-bottom--24">
        <v-card-title class="spacing-bottom--16"
          >{{
            override === "no"
              ? $t("resources.domains.create_subdomain_card_title")
              : $t("resources.domains.create_domain_title")
          }}
        </v-card-title>
        <va-text-input
          class="domain-text-input"
          v-model.trim="form.name"
          required
          :error-messages="errors.name"
          :label="$t('resources.domains.subdomain_name')"
          :placeholder="
            override === 'no'
              ? $t('resources.domains.subdomain_name_placeholder')
              : $t('resources.domains.create_domain_name_placeholder')
          " />
        <div class="input-hint">{{ $t("resources.domains.create_domain_name_hint") }}</div>
        <va-text-input
          class="domain-text-input prefixed"
          id="domain-url"
          v-model.trim="form.url"
          required
          :label="$t('resources.domains.url')"
          :rules="[rules.url]"
          :error-messages="errors.url"
          :placeholder="
            override === 'no'
              ? $t('resources.domains.create_subdomain_url_placeholder')
              : $t('resources.domains.create_domain_url_placeholder')
          ">
          <template #prepend-inner>
            <div class="prepend-wrapper">https://</div>
          </template>
        </va-text-input>
      </v-card>
    </va-form>
    <va-form v-if="step === 2" ref="form" :item="domain" v-model="form" :requiredFieldsSet="requiredFieldsSet">
      <v-card class="spacing-bottom--24 steps-card">
        <v-card-title class="steps-title">
          <div class="step-number">1</div>
          {{ $t("resources.domains.connect_domain_step_1_title") }}
        </v-card-title>
        <v-card-subtitle class="steps-description spacing-bottom--16">{{
          $t("resources.domains.connect_domain_step_1_content")
        }}</v-card-subtitle>
        <a
          :href="form.registrar ? form.registrar.helpLink : REGISTRAR_LIST.at(-1).helpLink"
          class="steps-link"
          target="_blank">
          {{ $t("resources.domains.connect_domain_step_1_instructions") }}
          <v-icon>mdi-chevron-right</v-icon>
        </a>
      </v-card>

      <v-card class="spacing-bottom--24 steps-card">
        <v-card-title class="steps-title">
          <div class="step-number">2</div>
          {{ $t("resources.domains.connect_domain_step_2_title") }}
        </v-card-title>
        <v-card-subtitle class="steps-description spacing-bottom--16">{{
          $t("resources.domains.connect_domain_step_2_content")
        }}</v-card-subtitle>
        <div v-for="item in savedDomain.dnsRecords" :key="item.value" class="input-wrapper spacing-bottom--16">
          <v-text-field
            :hide-details="item.type === 'ALIAS' ? 'auto' : true"
            disabled
            class="labeled hint-message"
            :hint="$t('resources.domains.connect_domain_step_2_alias_disclaimer')"
            persistent-hint
            :label="item.type"
            :value="item.name">
            <template #prepend-inner>
              <div class="prepend-wrapper">
                {{
                  item.type === "TXT"
                    ? $t("resources.domains.connect_domain_step_2_name")
                    : $t("resources.domains.connect_domain_step_2_host")
                }}
              </div>
            </template>
            <template #append>
              <div class="append-wrapper"><CopyText :value="item.name" /></div>
            </template>
          </v-text-field>
          <v-text-field hide-details disabled :value="item.value">
            <template #prepend-inner>
              <div class="prepend-wrapper">
                {{
                  item.type === "TXT"
                    ? $t("resources.domains.connect_domain_step_2_value")
                    : $t("resources.domains.connect_domain_step_2_target")
                }}
              </div>
            </template>
            <template #append>
              <div class="append-wrapper"><CopyText :value="item.value" /></div>
            </template>
          </v-text-field>
        </div>
      </v-card>
      <v-card class="spacing-bottom--24 steps-card">
        <v-card-title class="steps-title">
          <div class="step-number">3</div>
          {{ $t("resources.domains.connect_domain_step_3_title") }}
        </v-card-title>
        <v-card-subtitle class="steps-description spacing-bottom--16">{{
          $t("resources.domains.connect_domain_step_3_content")
        }}</v-card-subtitle>
        <v-checkbox
          :rules="[rules.required]"
          class="checkbox"
          hide-details="auto"
          color="primary"
          :label="$t('resources.domains.connect_domain_step_3_checkbox')"></v-checkbox>
      </v-card>
    </va-form>
  </div>
</template>

<script>
  import crudFormMixin from "@/mixins/crudFormMixin";
  import { DOMAIN_TYPE, REGISTRAR_LIST } from "@/constant";
  import Modal from "../custom/Modal.vue";
  import { urlValidation } from "@/utility/urlValidation";
  import CopyText from "../custom/CopyText.vue";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "NewDomainForm",
    mixins: [crudFormMixin],
    components: {
      Modal,
      CopyText,
    },
    props: {
      domain: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        requiredFormFields: ["registrar"],
        REGISTRAR_LIST,
        override: null,
        existingContent: null,
        overrideModalOpen: false,
        step: 1,
        form: {},
        savedDomain: {},
        errors: {},
        skipDelete: false,
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
          url: value => urlValidation(value) || this.$t("generic.validation.url"),
        },
      };
    },
    async beforeDestroy() {
      if (this.step === 2 && !this.skipDelete) {
        try {
          await this.$admin.dataProvider.delete("domains", {
            id: this.savedDomain.id,
          });
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          await this.$store.dispatch("api/refresh", "domains");
        }
      }
    },
    methods: {
      async submit() {
        if (this.step === 1) {
          if (!this.$refs.form.$refs.form.validate()) {
            return;
          }
          if (this.override === "yes") {
            this.overrideModalOpen = true;

            return;
          }
          await this.saveDomain();
          this.$emit("disableRedirect", true);
        } else {
          if (!this.$refs.form.$refs.form.validate()) {
            return;
          }
          this.skipDelete = true;
          this.$router.replace({
            name: "domains_show",
            params: { id: this.savedDomain.id },
            query: { tab: "domain_settings" },
          });
          this.$admin.toast.success(this.$t("resources.domains.connect_domain_success"));
        }
      },
      async confirmOverride() {
        this.overrideModalOpen = false;
        await this.saveDomain();
      },
      async saveDomain() {
        this.$emit("loading", true);
        try {
          const { data } = await this.$admin.dataProvider.create("domains", {
            data: {
              name: this.form.name,
              url: this.form.url,
              type: DOMAIN_TYPE.CUSTOM,
            },
          });
          this.savedDomain = data;
          this.step = 2;
        } catch (e) {
          this.errors = e.errors;
          this.$admin.toast.error(e.message || this.$t("generic.error"));
        } finally {
          this.$emit("loading", false);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .radio-card-wrapper {
    .radio-wrapper {
      margin-top: 0;
      padding: 0;
      &::v-deep {
        legend {
          @include text-body-2-bold();
          color: get-color("subtitle");
        }
        .v-messages__message {
          margin-top: 8px;
        }
      }

      .radio-input {
        &::v-deep {
          label {
            @include text-body-2();
            color: get-color("body");
          }

          .v-icon {
            color: get-color("icon");
          }
        }
        &:not(:last-child) {
          margin-bottom: 12px;
        }
      }
    }
  }

  .domain-text-input {
    &:not(:last-child) {
      margin-bottom: 8px;
    }

    &::v-deep {
      .v-input__slot {
        align-items: stretch;

        .v-input__append-inner {
          padding: 0 !important;
        }

        .v-input__prepend-inner {
          align-self: stretch !important;
          padding: 0;
        }
      }
      &.prefixed {
        .v-text-field__slot {
          input {
            margin-left: 64px;
          }
        }

        .v-input__prepend-inner {
          width: 0;
        }
      }
    }
  }

  .prepend-wrapper {
    background: get-color("element-bg-200");
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 3px 0 0 3px;
    color: get-color("body");
  }

  .input-hint {
    @include text-caption();
    color: get-color("subtitle");
    margin-bottom: 16px;
  }

  .content-override-modal-title {
    @include heading-4();
    margin-bottom: 4px;
    color: get-color("headline");
    margin-top: -4px;
  }

  .content-override-modal-content {
    @include text-subtitle-1();
    color: get-color("subtitle");
  }

  .steps-card {
    padding: 16px 16px 24px 24px;
    .steps-title {
      @include text-body-1-bold();
      color: get-color("headline");
      margin-bottom: 16px;
    }

    .steps-description {
      @include text-body-2();
      color: get-color("body");
      max-width: unset;
    }

    .steps-link {
      @include text-button-small();
      color: get-color("secondary-btn");
      display: flex;
      align-items: center;
      gap: 4px;
      text-decoration: none;

      &:hover {
        color: get-color("primary-hover");

        i {
          color: get-color("primary-hover");
        }
      }

      i {
        transition: none;
        color: get-color("secondary-btn");
        font-size: 16px;
      }
    }

    .step-number {
      position: absolute;
      left: -11px;
      @include text-caption-bold();
      height: 24px;
      width: 24px;
      background-color: get-color("primary-20");
      color: get-color("secondary-btn");
      border-radius: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .prepend-wrapper {
      background: get-color("element-bg-200");
      display: flex;
      align-items: center;
      padding: 8px;
      border-radius: 3px 0 0 3px;
      color: get-color("body");
    }

    .append-wrapper {
      @include text-button-small();
      display: flex;
      align-items: center;
      padding: 12px;
      border-radius: 0 3px 3px 0;
      color: get-color("primary");
      cursor: pointer;
    }

    &::v-deep {
      .v-input__slot {
        align-items: stretch;
        background-color: get-color("white") !important;
      }

      .v-input__append-inner {
        padding: 0 !important;
        margin: 0;
      }

      .v-input__prepend-inner {
        align-self: stretch !important;
        padding: 0;
        margin: 0;
      }

      .labeled {
        .v-input__prepend-inner {
          width: 0;
        }
        .v-text-field__slot {
          input {
            margin-left: 45px;
          }
        }
      }

      .hint-message {
        .v-messages__message {
          @include text-caption();
          color: get-color("subtitle");
        }
      }
    }

    .input-wrapper {
      display: flex;
      gap: 16px;

      &::v-deep {
        .v-text-field {
          pointer-events: unset;
          flex: 1;
        }
      }
    }

    .checkbox {
      &::v-deep {
        label {
          @include text-body-2();
          color: get-color("body") !important;
        }
      }
    }
  }
</style>
